import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const API_BASE_URL = 'https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      console.log('Sales Dashboard API: Token obtained successfully');
      return idToken;
    }

    console.warn('Sales Dashboard API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Sales Dashboard API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `Sales Dashboard API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Sales Dashboard API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_sales_analytics',
        `Sales Analytics API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[SaleAnalyticsApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(
      `Sales Dashboard API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_sales_analytics',
      `Sales Analytics API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[SaleAnalyticsApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * Builds query string from parameters object.
 * @param {Object} params - Object with query parameters.
 * @returns {string} - Query string (without leading '?').
 */
const buildQueryString = (params) => {
  const queryParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

/**
 * API utility functions for Sales Dashboard.
 */
export const salesDashboardApi = {
  /**
   * Retrieves sales dashboard data with comprehensive analytics.
   * GET /sales/dashboard
   * Lambda: innoworx-sales-dashboard
   * @param {Object} params - Query parameters for the dashboard
   * @param {string} [params.period] - Time period: 'daily', 'weekly', 'monthly', 'custom' (default: 'daily')
   * @param {string} [params.date] - Specific date (ISO format YYYY-MM-DD) - used when period=daily/weekly/monthly
   * @param {string} [params.start_date] - Start date (ISO format YYYY-MM-DD) - required when period='custom'
   * @param {string} [params.end_date] - End date (ISO format YYYY-MM-DD) - required when period='custom'
   * @param {string} [params.site_id] - Filter by specific site
   * @param {number} [params.limit] - Limit for top N results (default: 10, max: 50)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  getSalesDashboard: async (params = {}) => {
    try {
      // Validate period type
      const validPeriods = ['daily', 'weekly', 'monthly', 'custom'];
      if (params.period && !validPeriods.includes(params.period)) {
        throw new Error(
          'Invalid period type. Must be: daily, weekly, monthly, or custom'
        );
      }

      // Validate custom period requires start_date and end_date
      if (params.period === 'custom') {
        if (!params.start_date || !params.end_date) {
          throw new Error(
            'start_date and end_date are required when period=custom'
          );
        }
      }

      // Build query parameters
      const queryParams = {};

      if (params.period) queryParams.period = params.period;
      if (params.date) queryParams.date = params.date;
      if (params.start_date) queryParams.start_date = params.start_date;
      if (params.end_date) queryParams.end_date = params.end_date;
      if (params.site_id) queryParams.site_id = params.site_id;
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = parseInt(params.limit, 10);
      }

      const queryString = buildQueryString(queryParams);
      const endpoint = `/sales/dashboard${queryString ? `?${queryString}` : ''}`;

      console.log(
        'Sales Dashboard API: Fetching dashboard data with params:',
        JSON.stringify(queryParams, null, 2)
      );

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Sales Dashboard API: Dashboard response received');

      return {
        success: true,
        data: responseData,
        period: responseData.period,
        summary: responseData.summary,
        topCustomers: responseData.top_customers,
        topProducts: responseData.top_products,
        leastSoldProducts: responseData.least_sold_products,
        revenueOverTime: responseData.revenue_over_time,
        profitMarginOverTime: responseData.profit_margin_over_time,
        customersByOrderCount: responseData.customers_by_order_count,
        siteBreakdown: responseData.site_breakdown,
        hourlySales: responseData.hourly_sales,
        productCategoryBreakdown: responseData.product_category_breakdown,
        productionMetrics: responseData.production_metrics,
        topProducedItems: responseData.top_produced_items,
        productionOverTime: responseData.production_over_time,
        productionVsSales: responseData.production_vs_sales,
        productionCostBreakdown: responseData.production_cost_breakdown,
      };
    } catch (error) {
      console.error('Sales Dashboard API: Error in getSalesDashboard:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch sales dashboard data',
        status: error.status,
      };
    }
  },

  /**
   * Convenience method for daily sales dashboard.
   * @param {string} [date] - Date in YYYY-MM-DD format (defaults to today)
   * @param {Object} [options] - Additional options
   * @param {string} [options.site_id] - Filter by site
   * @param {number} [options.limit] - Limit for top/least lists
   * @returns {Promise<Object>} Dashboard data
   */
  getDailyDashboard: async (date = null, options = {}) => {
    const params = {
      period: 'daily',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return salesDashboardApi.getSalesDashboard(params);
  },

  /**
   * Convenience method for weekly sales dashboard.
   * @param {string} [date] - Date in YYYY-MM-DD format (defaults to current week)
   * @param {Object} [options] - Additional options
   * @param {string} [options.site_id] - Filter by site
   * @param {number} [options.limit] - Limit for top/least lists
   * @returns {Promise<Object>} Dashboard data
   */
  getWeeklyDashboard: async (date = null, options = {}) => {
    const params = {
      period: 'weekly',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return salesDashboardApi.getSalesDashboard(params);
  },

  /**
   * Convenience method for monthly sales dashboard.
   * @param {string} [date] - Date in YYYY-MM-DD format (defaults to current month)
   * @param {Object} [options] - Additional options
   * @param {string} [options.site_id] - Filter by site
   * @param {number} [options.limit] - Limit for top/least lists
   * @returns {Promise<Object>} Dashboard data
   */
  getMonthlyDashboard: async (date = null, options = {}) => {
    const params = {
      period: 'monthly',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return salesDashboardApi.getSalesDashboard(params);
  },

  /**
   * Convenience method for custom date range sales dashboard.
   * @param {string} startDate - Start date in YYYY-MM-DD format (required)
   * @param {string} endDate - End date in YYYY-MM-DD format (required)
   * @param {Object} [options] - Additional options
   * @param {string} [options.site_id] - Filter by site
   * @param {number} [options.limit] - Limit for top/least lists
   * @returns {Promise<Object>} Dashboard data
   */
  getCustomRangeDashboard: async (startDate, endDate, options = {}) => {
    const params = {
      period: 'custom',
      start_date: startDate,
      end_date: endDate,
      ...options,
    };

    return salesDashboardApi.getSalesDashboard(params);
  },
};

